import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
	getOrderEmailSubjectTemplates,
	type OrderEmailSubjectCatalog,
	saveOrderEmailSubjectTemplates,
} from "../../../services/EmailSerivce";
import PermissionGate from "../../controls/auth/PermissionGate";

const Shell = styled.section`
	margin: 0 0 8px;
	max-width: 1080px;
`;

const Intro = styled.div`
	margin-bottom: 20px;

	h2 {
		margin: 0 0 6px;
		font-size: 1.15rem;
		font-weight: 700;
		color: #0f172a;
		line-height: 1.3;
	}

	p {
		margin: 0;
		font-size: 14px;
		color: #64748b;
		line-height: 1.5;
		max-width: 62ch;
	}
`;

const PageLayout = styled.div`
	display: grid;
	grid-template-columns: 1fr minmax(280px, 340px);
	gap: 24px;
	align-items: start;

	@media (max-width: 960px) {
		grid-template-columns: 1fr;
	}
`;

const FormColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
`;

const PreviewColumn = styled.aside`
	position: sticky;
	top: 12px;

	@media (max-width: 960px) {
		position: relative;
		top: auto;
		order: -1;
	}
`;

const PreviewCard = styled.div`
	background: linear-gradient(180deg, #f8fafc 0%, #ffffff 48%);
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
	overflow: hidden;
`;

const PreviewBar = styled.div`
	padding: 10px 14px;
	background: #1e293b;
	color: #e2e8f0;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
`;

const PreviewBody = styled.div`
	padding: 14px 14px 16px;
`;

const PreviewHint = styled.p`
	margin: 0 0 12px;
	font-size: 12px;
	color: #64748b;
	line-height: 1.45;
`;

const PreviewRow = styled.div`
	margin-bottom: 12px;

	&:last-child {
		margin-bottom: 0;
	}
`;

const PreviewKicker = styled.div`
	font-size: 11px;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 4px;
`;

const PreviewSubject = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #0f172a;
	line-height: 1.4;
	word-break: break-word;
	font-family: ui-sans-serif, system-ui, sans-serif;
	padding: 8px 10px;
	background: #f1f5f9;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
`;

const EnvNote = styled.p`
	margin: 12px 0 0;
	padding: 8px 10px;
	font-size: 11px;
	line-height: 1.45;
	color: #64748b;
	background: #fefce8;
	border: 1px solid #fef08a;
	border-radius: 8px;
`;

const PreviewDivider = styled.div`
	margin: 16px 0 12px;
	border-top: 1px solid #e2e8f0;
	position: relative;

	span {
		position: absolute;
		top: -9px;
		left: 50%;
		transform: translateX(-50%);
		background: #f8fafc;
		padding: 0 8px;
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: #94a3b8;
		white-space: nowrap;
	}
`;

const RecipientGroup = styled.div`
	margin-bottom: 14px;

	&:last-child {
		margin-bottom: 0;
	}
`;

const RecipientGroupTitle = styled.div`
	font-size: 11px;
	font-weight: 700;
	color: #475569;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 8px;
	padding: 4px 8px;
	background: #f1f5f9;
	border-radius: 6px;
	border-left: 3px solid #6366f1;
`;

const Card = styled.div`
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 16px 16px 18px;
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const CardTitle = styled.h3`
	margin: 0 0 4px;
	font-size: 14px;
	font-weight: 700;
	color: #1e293b;
`;

const CardDesc = styled.p`
	margin: 0 0 14px;
	font-size: 12px;
	color: #94a3b8;
	line-height: 1.45;
`;

const FieldGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const FieldGrid2 = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px 14px;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const Label = styled.label`
	font-size: 13px;
	font-weight: 600;
	color: #334155;
	display: block;
	margin-bottom: 4px;
	line-height: 1.35;
`;

const Hint = styled.span`
	display: block;
	margin-top: 2px;
	font-size: 11px;
	font-weight: 500;
	color: #94a3b8;
	line-height: 1.35;
`;

const Input = styled.input`
	width: 100%;
	box-sizing: border-box;
	padding: 9px 11px;
	font-size: 14px;
	border: 1px solid #d1d5db;
	border-radius: 8px;
	color: #0f172a;
	outline: none;

	&:focus {
		border-color: #6366f1;
		box-shadow: 0 0 0 2px #e0e7ff;
	}
`;

const KeyTag = styled.span`
	display: inline-block;
	margin-left: 6px;
	font-size: 10px;
	font-weight: 600;
	color: #cbd5e1;
	font-family: ui-monospace, monospace;
	vertical-align: 1px;
`;

const SubGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px 14px;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	padding-top: 4px;
`;

const SaveBtn = styled.button`
	padding: 10px 18px;
	border-radius: 8px;
	border: none;
	background: #4f46e5;
	color: #ffffff;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s;

	&:hover:not(:disabled) {
		background: #4338ca;
	}
	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
`;

const Status = styled.span<{ $ok?: boolean }>`
	font-size: 13px;
	font-weight: 600;
	color: ${({ $ok }) => ($ok ? "#15803d" : "#b91c1c")};
`;

const InlineHelp = styled.details`
	margin-bottom: 4px;
	font-size: 12px;
	color: #64748b;

	summary {
		cursor: pointer;
		font-weight: 600;
		color: #475569;
		user-select: none;
	}

	&[open] summary {
		margin-bottom: 8px;
	}

	code {
		font-size: 11px;
		background: #f1f5f9;
		padding: 1px 5px;
		border-radius: 4px;
		color: #0f172a;
	}
`;

const defaultCatalog: OrderEmailSubjectCatalog = {
	bookingConfirmed: "",
	orderCanceled: "",
	single: "",
	double: "",
	triple: "",
	manyFields: "",
	labels: {
		date: "",
		time: "",
		address: "",
		quantity: "",
		price: "",
	},
};

const LABEL_ROWS: Array<{
	key: keyof OrderEmailSubjectCatalog["labels"];
	title: string;
	hint: string;
}> = [
	{ key: "date", title: "Date", hint: "Start/end calendar date" },
	{ key: "time", title: "Time", hint: "Same day, time only" },
	{ key: "address", title: "Station", hint: "Station change" },
	{ key: "quantity", title: "Quantity", hint: "Number of bags" },
	{ key: "price", title: "Price", hint: "Total amount" },
];

function applySubjectTemplate(template: string, labels: OrderEmailSubjectCatalog["labels"]): string {
	if (!template?.trim()) {
		return "—";
	}
	const date = labels.date?.trim() || "Date";
	const time = labels.time?.trim() || "Time";
	const address = labels.address?.trim() || "Address";
	return template
		.replace(/\$\{label\}/g, date)
		.replace(/\$\{labelA\}/g, date)
		.replace(/\$\{labelB\}/g, time)
		.replace(/\$\{labelC\}/g, address)
		.replace(/\$\{count\}/g, "5");
}

/** Strip the "WhaleLO - " (or en-dash / em-dash) prefix to derive the email body title. */
function stripSubjectPrefix(subject: string): string {
	return subject.replace(/^WhaleLO\s*[-–—]\s*/i, "").trim() || subject;
}

export default function OrderEmailSubjectTemplatesPanel() {
	const [catalog, setCatalog] = useState<OrderEmailSubjectCatalog>(defaultCatalog);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [loadErr, setLoadErr] = useState("");
	const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setLoadErr("");
		setStatus(null);
		try {
			const data = await getOrderEmailSubjectTemplates();
			setCatalog({
				...defaultCatalog,
				...data,
				labels: { ...defaultCatalog.labels, ...(data.labels || {}) },
			});
		} catch {
			setLoadErr("Could not load. Try again.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const previews = useMemo(() => {
		const { labels } = catalog;
		// For recipient-specific previews we swap ${label} with the relevant label word
		const dateLabels = { ...labels };
		const addressLabels = { ...labels, date: labels.address || "Address" };

		const single = applySubjectTemplate(catalog.single, labels);
		const double = applySubjectTemplate(catalog.double, labels);
		const triple = applySubjectTemplate(catalog.triple, labels);
		const many = applySubjectTemplate(catalog.manyFields, labels);

		return {
			confirmed: applySubjectTemplate(catalog.bookingConfirmed, labels),
			canceled: applySubjectTemplate(catalog.orderCanceled, labels),
			single,
			double,
			triple,
			many,
			// Recipient-specific previews
			customerDate: applySubjectTemplate(catalog.single, dateLabels),
			customerAddress: applySubjectTemplate(catalog.single, addressLabels),
			stationCanceled: applySubjectTemplate(catalog.orderCanceled, labels),
			stationDate: applySubjectTemplate(catalog.single, dateLabels),
			// Body title previews (subject minus "WhaleLO - " prefix)
			bodySingle: stripSubjectPrefix(single),
			bodyDouble: stripSubjectPrefix(double),
			bodyTriple: stripSubjectPrefix(triple),
			bodyMany: stripSubjectPrefix(many),
		};
	}, [catalog]);

	const setMain = (key: keyof OrderEmailSubjectCatalog, value: string) => {
		if (key === "labels") return;
		setCatalog((c) => ({ ...c, [key]: value }));
	};

	const setLabel = (k: string, value: string) => {
		setCatalog((c) => ({
			...c,
			labels: { ...c.labels, [k]: value },
		}));
	};

	const handleSave = async () => {
		setSaving(true);
		setStatus(null);
		try {
			const res = await saveOrderEmailSubjectTemplates(catalog);
			if (res?.data) {
				setCatalog({
					...defaultCatalog,
					...res.data,
					labels: { ...defaultCatalog.labels, ...(res.data.labels || {}) },
				});
			}
			setStatus({ ok: true, text: "Saved." });
		} catch (e: unknown) {
			const msg =
				(e as { response?: { data?: { message?: string } } })?.response?.data?.message || "Save failed.";
			setStatus({ ok: false, text: msg });
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return <EmptyMsg>Loading…</EmptyMsg>;
	}

	if (loadErr) {
		return (
			<EmptyMsg>
				{loadErr}
				<br />
				<Retry
					type="button"
					onClick={() => void load()}>
					Retry
				</Retry>
			</EmptyMsg>
		);
	}

	return (
		<Shell aria-labelledby="order-email-subject-heading">
			<Intro>
				<h2 id="order-email-subject-heading">Tiêu đề email đơn hàng</h2>
				<p>
					Chỉnh dòng <strong>Subject</strong> trong inbox (thanh toán, hủy, cập nhật booking). Nội dung thư
					vẫn sửa ở các tab template phía trước.
				</p>
			</Intro>

			<PageLayout>
				<FormColumn>
					<InlineHelp>
						<summary>Biến placeholder &amp; bật API</summary>
						<div>
							Dùng <code>{"${label}"}</code>, <code>{"${labelA}"}</code>, <code>{"${labelB}"}</code>,{" "}
							<code>{"${labelC}"}</code>, <code>{"${count}"}</code>. Ô <em>Từ cho từng loại thay đổi</em>{" "}
							điền nghĩa hiển thị trong tiêu đề. Backend cần <code>EMAIL_DYNAMIC_SUBJECT</code> bật thì
							mới áp dụng; nếu không, server dùng subject cố định cũ.
						</div>
					</InlineHelp>

					<Card>
						<CardTitle>Giao dịch cơ bản</CardTitle>
						<CardDesc>Sau thanh toán hoặc khi hủy — không có danh sách “đã đổi gì”.</CardDesc>
						<FieldGrid2>
							<div>
								<Label>Đã thanh toán</Label>
								<Hint>Khách đã pay, mail xác nhận</Hint>
								<Input
									value={catalog.bookingConfirmed}
									onChange={(e) => setMain("bookingConfirmed", e.target.value)}
									aria-label="Payment confirmed subject"
								/>
							</div>
							<div>
								<Label>Đơn hủy</Label>
								<Input
									value={catalog.orderCanceled}
									onChange={(e) => setMain("orderCanceled", e.target.value)}
									aria-label="Order canceled subject"
								/>
							</div>
						</FieldGrid2>
					</Card>

					<Card>
						<CardTitle>Sau khi sửa booking</CardTitle>
						<CardDesc>Một template cho mỗi số loại thay đổi (ngày, giờ, trạm, số kiện…).</CardDesc>
						<FieldGrid>
							<div>
								<Label>Một loại thay đổi</Label>
								<Hint>Ví dụ chỉ đổi trạm hoặc chỉ đổi ngày</Hint>
								<Input
									value={catalog.single}
									onChange={(e) => setMain("single", e.target.value)}
									placeholder="WhaleLO - ${label} change confirmed"
								/>
							</div>
							<FieldGrid2>
								<div>
									<Label>Hai loại</Label>
									<Input
										value={catalog.double}
										onChange={(e) => setMain("double", e.target.value)}
									/>
								</div>
								<div>
									<Label>Ba loại</Label>
									<Input
										value={catalog.triple}
										onChange={(e) => setMain("triple", e.target.value)}
									/>
								</div>
							</FieldGrid2>
							<div>
								<Label>Hơn ba loại</Label>
								<Hint>Dùng {"${count}"} cho số trường đã đổi</Hint>
								<Input
									value={catalog.manyFields}
									onChange={(e) => setMain("manyFields", e.target.value)}
								/>
							</div>
						</FieldGrid>
					</Card>

					<Card>
						<CardTitle>Từ thay thế trong tiêu đề</CardTitle>
						<CardDesc>
							Khớp với placeholder: hiển thị ngắn gọn (VD: Date, Address). Cột phải là preview mẫu với các
							từ này.
						</CardDesc>
						<SubGrid>
							{LABEL_ROWS.map((row) => (
								<div key={row.key}>
									<Label>
										{row.title}
										<KeyTag>{row.key}</KeyTag>
									</Label>
									<Hint>{row.hint}</Hint>
									<Input
										value={catalog.labels[row.key] || ""}
										onChange={(e) => setLabel(row.key, e.target.value)}
										aria-label={`Label word for ${row.key}`}
									/>
								</div>
							))}
						</SubGrid>
					</Card>

					<Actions>
						<PermissionGate permission="settings.manage">
							<SaveBtn
								type="button"
								onClick={() => void handleSave()}
								disabled={saving}>
								{saving ? "Saving…" : "Save subject lines"}
							</SaveBtn>
						</PermissionGate>
						{status ? <Status $ok={status.ok}>{status.text}</Status> : null}
					</Actions>
				</FormColumn>

				<PreviewColumn>
					<PreviewCard>
						<PreviewBar>Xem trước tiêu đề</PreviewBar>
						<PreviewBody>
							<PreviewHint>
								Ví dụ minh họa — hàng thật do backend ghép giống logic preview (nhãn
								Date/Time/Address…).
							</PreviewHint>
							<PreviewRow>
								<PreviewKicker>Đã thanh toán</PreviewKicker>
								<PreviewSubject>{previews.confirmed}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Hủy đơn</PreviewKicker>
								<PreviewSubject>{previews.canceled}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Một thay đổi</PreviewKicker>
								<PreviewSubject>{previews.single}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Hai thay đổi</PreviewKicker>
								<PreviewSubject>{previews.double}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Ba thay đổi</PreviewKicker>
								<PreviewSubject>{previews.triple}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Nhiều thay đổi (count = 5)</PreviewKicker>
								<PreviewSubject>{previews.many}</PreviewSubject>
							</PreviewRow>

							<PreviewDivider>
								<span>Theo người nhận</span>
							</PreviewDivider>

							<RecipientGroup>
								<RecipientGroupTitle>Khách hàng</RecipientGroupTitle>
								<PreviewRow>
									<PreviewKicker>Ngày thay đổi</PreviewKicker>
									<PreviewSubject>{previews.customerDate}</PreviewSubject>
								</PreviewRow>
								<PreviewRow>
									<PreviewKicker>Địa chỉ thay đổi</PreviewKicker>
									<PreviewSubject>{previews.customerAddress}</PreviewSubject>
								</PreviewRow>
							</RecipientGroup>

							<RecipientGroup>
								<RecipientGroupTitle>Trạm</RecipientGroupTitle>
								<PreviewRow>
									<PreviewKicker>Huỷ đơn</PreviewKicker>
									<PreviewSubject>{previews.stationCanceled}</PreviewSubject>
								</PreviewRow>
								<PreviewRow>
									<PreviewKicker>Ngày thay đổi</PreviewKicker>
									<PreviewSubject>{previews.stationDate}</PreviewSubject>
								</PreviewRow>
							</RecipientGroup>

							<PreviewDivider>
								<span>Body title (trong email)</span>
							</PreviewDivider>

							<PreviewHint>
								Tiêu đề bên trong nội dung email (header xanh) dùng cùng template, tự bỏ prefix
								&quot;WhaleLO - &quot;. Khi booking mới (Paid), body title vẫn là &quot;Booking
								Confirmed&quot;.
							</PreviewHint>

							<PreviewRow>
								<PreviewKicker>Body: Paid</PreviewKicker>
								<PreviewSubject>Booking Confirmed</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Body: 1 field</PreviewKicker>
								<PreviewSubject>{previews.bodySingle}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Body: 2 fields</PreviewKicker>
								<PreviewSubject>{previews.bodyDouble}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Body: 3 fields</PreviewKicker>
								<PreviewSubject>{previews.bodyTriple}</PreviewSubject>
							</PreviewRow>
							<PreviewRow>
								<PreviewKicker>Body: many fields</PreviewKicker>
								<PreviewSubject>{previews.bodyMany}</PreviewSubject>
							</PreviewRow>

							<EnvNote>
								Nếu <code>EMAIL_DYNAMIC_SUBJECT</code> tắt trên server, khách vẫn có thể thấy subject cố
								định cũ — kiểm tra env khi không thấy thay đổi.
							</EnvNote>
						</PreviewBody>
					</PreviewCard>
				</PreviewColumn>
			</PageLayout>
		</Shell>
	);
}

const EmptyMsg = styled.div`
	padding: 32px 24px;
	text-align: center;
	color: #64748b;
	font-size: 14px;
	background: #f8fafc;
	border-radius: 12px;
	border: 1px dashed #cbd5e1;
	max-width: 560px;
`;

const Retry = styled.button`
	margin-top: 12px;
	padding: 8px 16px;
	border-radius: 8px;
	border: 1px solid #cbd5e1;
	background: #fff;
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	&:hover {
		border-color: #6366f1;
		color: #4338ca;
	}
`;
